import { get, post } from './client'

// ── Types ─────────────────────────────────────────────────────────────────────

export type ForecastModel = 'linear' | 'weighted_recent' | 'moving_average'

export type ForecastTargetMonth = {
  revenue: number
  orders:  number
  qty:     number
}

export type ForecastSettings = {
  monthlyTargets:         Record<string, ForecastTargetMonth>
  quarterlyTargets:       Record<string, ForecastTargetMonth>
  halfYearTargets:        Record<string, ForecastTargetMonth>
  yearlyTargets:          Record<string, ForecastTargetMonth>
  fiscalYearStartMonth:   number
  desiredProfitPct:       number
  forecastModel:          ForecastModel
  workingDaysPerWeek:     number
  reachabilityMultiplier: number
}

export type PeriodSummary = {
  period:  string
  months:  string[]
  revenue: { gross: number; returns: number; net: number; ordersCount: number; returnsCount: number }
  cogs:    { total: number; knownCount: number; missingCount: number }
  profit:  { gross: number; net: number; grossMargin: number; netMargin: number }
  delivery:{ deliveredCount: number; deliveredRate: number; returnCount: number; returnRate: number }
  pacing:  { workingDaysTotal: number; workingDaysElapsed: number; workingDaysLeft: number; dailyRevenue: number; dailyOrders: number }
  targetProgress: {
    revenueTarget: number; revenueActual: number; revenuePct: number | null
    ordersTarget:  number; ordersActual:  number; ordersPct:  number | null
    qtyTarget:     number | null; qtyActual: number | null; qtyPct: number | null
  } | null
  projected: { projectedRevenue: number; projectedOrders: number; projectedProfit: number; modelUsed: ForecastModel }
  avgOrderValue: number
}

export type PeriodType = 'month' | 'quarter' | 'half' | 'year'

export type CurrentPeriods = {
  fiscalYearStartMonth: number
  month:   { key: string; label: string }
  quarter: { key: string; label: string; months: string[]; range: string }
  half:    { key: string; label: string; months: string[]; range: string }
  year:    { key: string; label: string; months: string[]; range: string }
}

export type ForecastSummary = {
  month: string
  revenue: {
    gross:        number
    returns:      number
    net:          number
    ordersCount:  number
    returnsCount: number
  }
  cogs: {
    total:        number
    knownCount:   number
    missingCount: number
  }
  profit: {
    gross:       number
    net:         number
    grossMargin: number
    netMargin:   number
  }
  delivery: {
    deliveredCount: number
    deliveredRate:  number
    returnCount:    number
    returnRate:     number
  }
  settlements: {
    settledCount:  number
    settledAmount: number
  }
  pacing: {
    workingDaysTotal:   number
    workingDaysElapsed: number
    workingDaysLeft:    number
    dailyRevenue:       number   // what the store is achieving per day
    dailyOrders:        number
    // What it must achieve per day to reach the target. null when no target is
    // set or the period is over — never confuse it with dailyRevenue (§9.9-3).
    requiredDailyRevenue: number | null
  }
  adSpendSource: AdSpendSource
  targetProgress: {
    revenueTarget: number
    revenueActual: number
    revenuePct:    number | null
    ordersTarget:  number
    ordersActual:  number
    ordersPct:     number | null
    qtyTarget:     number | null
    qtyActual:     number | null
    qtyPct:        number | null
  } | null
  projected: {
    projectedRevenue: number
    projectedOrders:  number
    projectedProfit:  number
    // Operating profit less the month's fixed expenses — the projected bottom
    // line, not just the contribution (§9.4).
    projectedNetProfit:   number
    fixedExpenses:        number
    // Scenario band from this store's own daily volatility, not a fixed ±%.
    bestCase:             number
    worstCase:            number
    dailyStdDev:          number
    volatilitySampleDays: number
    modelUsed:        ForecastModel
  }
}

export type AdSpendSource = {
  mode:    'dt_mo' | 'monthly_budget' | 'daily_manual'
  active:  boolean
  warning: string | null
}

export type DecisionEngineResult = {
  month:           string
  currentPosition: {
    revenue:         number
    totalOrders:     number
    netProfit:       number
    netMargin:       number
    dailyRevenue:    number
    modelUsed:       ForecastModel
    workingDaysLeft: number
  }
  breakEven: {
    additionalRevenueNeeded:  number
    additionalOrdersNeeded:   number
    estimatedDaysToBreakEven: number
    alreadyBreakEven:         boolean
  }
  monthlyTarget: {
    revenueTarget:          number
    ordersTarget:           number
    revenueGap:             number
    ordersGap:              number
    requiredDailyRevenue:   number
    requiredDailyOrders:    number
    isTargetReachable:      boolean
    reachabilityMultiplier: number
    workingDaysLeft:        number
    hasTarget:              boolean
  }
  profitTarget: {
    desiredProfitPct:        number
    desiredProfitAmount:     number
    currentProfit:           number
    additionalRevenueNeeded: number | null
    additionalOrdersNeeded:  number | null
    alreadyHit:              boolean
  }
}

export type SkuBreakdownItem = {
  sku:          string
  qty:          number
  totalCost:    number
  deliveredQty: number
}

export type CategoryBreakdown = {
  month:     string
  totalSkus: number
  breakdown: SkuBreakdownItem[]
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function qs(shop: string, params: Record<string, string> = {}): string {
  return `?${new URLSearchParams({ shop, ...params }).toString()}`
}

// ── API ───────────────────────────────────────────────────────────────────────

export const foApi = {
  getSettings: (shop: string): Promise<ForecastSettings> =>
    get(`/forecast/settings${qs(shop)}`),

  saveSettings: (shop: string, settings: Partial<ForecastSettings>): Promise<ForecastSettings> =>
    post(`/forecast/settings${qs(shop)}`, settings),

  getSummary: (shop: string, month?: string): Promise<ForecastSummary> => {
    const params: Record<string, string> = {}
    if (month) params.month = month
    return get(`/forecast/summary${qs(shop, params)}`)
  },

  getDecisionEngine: (shop: string, month?: string): Promise<DecisionEngineResult> => {
    const params: Record<string, string> = {}
    if (month) params.month = month
    return get(`/forecast/decision-engine${qs(shop, params)}`)
  },

  getCategoryBreakdown: (shop: string, month?: string): Promise<CategoryBreakdown> => {
    const params: Record<string, string> = {}
    if (month) params.month = month
    return get(`/forecast/category-breakdown${qs(shop, params)}`)
  },

  getPeriodSummary: (shop: string, period: string): Promise<PeriodSummary> =>
    get(`/forecast/period-summary${qs(shop, { period })}`),

  getCurrentPeriods: (shop: string): Promise<CurrentPeriods> =>
    get(`/forecast/current-periods${qs(shop)}`),
}
